import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import React from 'react';
import { caseDetails, allWorks } from '@/mocks/portfolio';
import Navbar from '@/pages/home/components/Navbar';
import SlotText from '@/components/feature/SlotText';
import ImageViewer, { isTextCover } from '@/components/feature/ImageViewer';

// 動画プレーヤー（mp4直接再生）
type CaseVideo = { src?: string; caption: string };

function VideoEmbed({ video }: { video: CaseVideo }) {
  const [hasError, setHasError] = useState(false);
  const { src, caption } = video;
  const isUnsupportedMov = !!src && /\.mov($|\?)/i.test(src);

  return (
    <div className="mb-8">
      <div className="w-full bg-foreground-950 overflow-hidden relative" style={{ aspectRatio: '16/9' }}>
        {src && !isUnsupportedMov && !hasError ? (
          <video
            src={src}
            controls
            preload="metadata"
            className="w-full h-full object-contain"
            onError={() => setHasError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-foreground-400 bg-foreground-950">
            <i className="ri-video-line w-10 h-10 flex items-center justify-center text-4xl" />
            <p className="font-label text-[11px] tracking-[0.12em] uppercase">
              動画を準備中です
            </p>
          </div>
        )}
      </div>
      <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-2">
        {caption}
      </p>
    </div>
  );
}

type GalleryImage = { src: string; caption: string; large?: boolean };

function GalleryImg({ src, alt, className }: { src: string; alt: string; className: string }) {
  const [broken, setBroken] = React.useState(false);
  if (broken) return null;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setBroken(true)}
    />
  );
}

function buildGalleryBlocks(images: GalleryImage[]) {
  const blocks: { kind: 'large' | 'pair'; items: GalleryImage[] }[] = [];
  let pair: GalleryImage[] = [];
  for (const img of images) {
    if (img.large) {
      if (pair.length) {
        blocks.push({ kind: 'pair', items: pair });
        pair = [];
      }
      blocks.push({ kind: 'large', items: [img] });
    } else {
      pair.push(img);
      if (pair.length === 2) {
        blocks.push({ kind: 'pair', items: pair });
        pair = [];
      }
    }
  }
  if (pair.length) blocks.push({ kind: 'pair', items: pair });
  return blocks;
}

export default function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Redirect legacy movie-only cases to their unified main cases
  useEffect(() => {
    if (id === 'saitama-wood-movie') {
      navigate('/case/saitama-wood-terrace', { replace: true });
    }
    if (id === 'aqua-park-movie') {
      navigate('/case/aqua-park-shinagawa', { replace: true });
    }
  }, [id, navigate]);

  const caseItem = caseDetails.find((c) => c.id === id);

  // Always scroll to top when entering a case detail page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!caseItem) {
    return (
      <div className="min-h-screen bg-background-50 text-foreground-950 font-body">
        <Navbar />
        <div className="mx-auto max-w-[1280px] px-6 md:px-10 pt-32 pb-20">
          <p className="text-lg text-foreground-700">Case not found.</p>
          <Link
            to="/"
            className="mt-4 inline-block text-foreground-500 hover:text-foreground-950 font-label text-sm tracking-[0.12em] uppercase"
          >
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  const {
    project,
    client,
    year,
    category,
    role,
    issue,
    challenge,
    insight,
    strategy,
    experience,
    output,
    impact,
    images,
  } = caseItem;

  // Independent-site migration: remove only the transparent placeholder entries
  // created for dead remote image references, so gallery items reflow without blank gaps.
  const visibleImages = images.filter(
    (img) => !img.src.includes('missing-transparent.gif')
  );

  // Find next project in allWorks array
  const currentIndex = allWorks.findIndex((w) => w.id === id);
  const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % allWorks.length : 0;
  const nextWork = allWorks[nextIndex];

  // Hero images: hitachi-solutions shows first two side-by-side
  const heroImages = id === 'hitachi-solutions' && visibleImages[1] ? visibleImages.slice(0, 2) : visibleImages.slice(0, 1);
  const galleryStartIndex = id === 'hitachi-solutions' && visibleImages[1] ? 2 : 1;
  const galleryImages = visibleImages.slice(galleryStartIndex);
  const hasLargeImages = galleryImages.some((img) => img.large);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950 font-body">
      <Navbar />
      <main className="pt-24 md:pt-28 pb-20">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10">
          {/* Back to works list */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              sessionStorage.setItem('scrollToArchive', 'true');
              navigate('/');
            }}
            className="inline-flex items-center gap-2 font-label text-[11px] tracking-[0.12em] uppercase text-foreground-500 hover:text-foreground-950 transition-colors mb-10 cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            All Works
          </a>

          {/* Meta row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8 pb-8 border-b border-background-300">
            <div>
              <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-400 mb-1">Project</p>
              <SlotText
                as="p"
                text={project}
                className="font-heading text-lg md:text-xl text-foreground-950 tracking-[-0.02em]"
              />
            </div>
            <div>
              <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-400 mb-1">Client</p>
              <p className="text-sm text-foreground-700">{client}</p>
            </div>
            <div>
              <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-400 mb-1">Year</p>
              <p className="text-sm text-foreground-700">{year}</p>
            </div>
            <div>
              <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-400 mb-1">Category</p>
              <p className="text-sm text-foreground-700">{category}</p>
            </div>
          </div>

          <div className="mb-6 pb-6 border-b border-background-300">
            <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-400 mb-1">My Role</p>
            <p className="text-sm md:text-base text-foreground-800">{role}</p>
          </div>

          {/* Hero images */}
          {heroImages.length > 0 && (
            <div className="w-full mb-12 overflow-hidden">
              {heroImages.length === 2 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {heroImages.map((img) => (
                    <img
                      key={img.src}
                      src={img.src}
                      alt={img.caption || project}
                      className="w-full h-auto object-cover"
                    />
                  ))}
                </div>
              ) : (
                <img
                  src={heroImages[0].src}
                  alt={heroImages[0].caption || project}
                  className={`w-full block ${
                    id === 'hitachi-solutions'
                      ? 'h-[55vh] object-cover object-[center_30%]'
                      : id === 'aizu-coin'
                      ? 'h-auto object-contain object-top bg-background-50'
                      : 'h-auto object-cover object-center'
                  }`}
                  style={{ minHeight: id === 'chefs-pride' ? '1px' : undefined }}
                />
              )}
            </div>
          )}

          {/* Story flow — concept → issue → solution → strategy → design landing */}
          <div className="mb-16">
            <div className="mb-8 pb-4 border-b border-background-300 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
                Case Story — 課題からデザイン着地まで
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
              {/* Left narrative column */}
              <div className="md:col-span-8 space-y-0">
                {[
                  { no: '01', en: 'Concept / Context', ja: 'コンセプト・背景', body: issue },
                  { no: '02', en: 'Issue', ja: '課題の抽出', body: challenge },
                  { no: '03', en: 'Insight', ja: '本質の洞察', body: insight },
                  { no: '04', en: 'Strategy', ja: '戦略', body: strategy },
                ]
                  .filter((s) => s.body)
                  .map((s, i) => (
                    <div
                      key={s.no}
                      className={`flex gap-5 md:gap-6 ${i > 0 ? 'mt-8 pt-8 border-t border-background-200' : ''}`}
                    >
                      <span className="font-heading fw-light text-3xl md:text-4xl text-foreground-950 leading-none tracking-[-0.03em] flex-shrink-0">
                        {s.no}
                      </span>
                      <div>
                        <div className="mb-2 flex items-baseline gap-2">
                          <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-950">
                            {s.en}
                          </p>
                          <span className="font-label text-[9px] tracking-[0.08em] uppercase text-foreground-400">
                            {s.ja}
                          </span>
                        </div>
                        <p className="text-base md:text-lg text-foreground-800 leading-relaxed">{s.body}</p>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Right output / design landing column */}
              <div className="md:col-span-4 space-y-6">
                {experience && (
                  <div className="bg-background-100 border border-background-300 p-5">
                    <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-950 mb-2">
                      05 · Design Landing
                    </p>
                    <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-950 mb-2">
                      Experience / Touchpoint
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed">{experience}</p>
                  </div>
                )}
                {output && (
                  <div className="bg-background-100 border border-background-300 p-5">
                    <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-950 mb-2">
                      Output
                    </p>
                    <p className="text-sm text-foreground-700 leading-relaxed">{output}</p>
                  </div>
                )}
                {impact && (
                  <div className="bg-primary-50 border border-primary-200 p-5">
                    <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-950 mb-2">
                      Impact / Value
                    </p>
                    <p className="text-sm text-foreground-800 leading-relaxed font-medium">{impact}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Image gallery — dynamic, all images */}
          {galleryImages.length > 0 && (
            <div className="space-y-0">
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-6 pb-4 border-b border-background-300">
                Project Images — {galleryImages.length} images
              </p>

              {id === 'yanjan-blue-membership' || id === 'unite-card-tvcm' || id === 'baby-me-belk' || id === 'aqua-park-shinagawa' || id === 'unite-card' || id === 'reganet' || id === 'plus-keshipon' || id === 'marubeni-packetmate' ? (
                // Vertical stacked layout for yanjan-blue-membership — no side-by-side
                galleryImages.map((img, idx) => (
                  <div key={idx} className="mb-1">
                    <div className="overflow-hidden bg-background-200">
                      <GalleryImg
                        src={img.src}
                        alt={img.caption}
                        className="w-full h-auto object-cover object-center"
                      />
                    </div>
                    <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-2 mb-6">
                      {img.caption}
                    </p>
                  </div>
                ))
              ) : hasLargeImages ? (
                // Document layout — large images full-width, others side-by-side pairs
                buildGalleryBlocks(galleryImages).map((block, bi) =>
                  block.kind === 'large' ? (
                    <div key={`large-${bi}`} className="mb-2">
                      <div className="overflow-hidden bg-background-200">
                        <GalleryImg
                          src={block.items[0].src}
                          alt={block.items[0].caption}
                          className="w-full h-auto object-cover object-center"
                        />
                      </div>
                      <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-2 mb-8">
                        {block.items[0].caption}
                      </p>
                    </div>
                  ) : (
                    <div key={`pair-${bi}`} className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-1">
                      {block.items.map((img, ii) => (
                        <div key={ii}>
                          <div className="overflow-hidden bg-background-200">
                            <GalleryImg
                              src={img.src}
                              alt={img.caption}
                              className="w-full h-auto object-cover object-center"
                            />
                          </div>
                          <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-2 mb-6">
                            {img.caption}
                          </p>
                        </div>
                      ))}
                    </div>
                  )
                )
              ) : (
                // Default mixed layout — full width + side-by-side pairs
                galleryImages.map((img, idx) => {
                  const isLastImage = idx === galleryImages.length - 1;
                  const isFullWidth = idx % 3 === 0 || (isLastImage && idx % 3 !== 1 && idx % 3 !== 2) || (isLastImage && (galleryImages.length - 1) % 3 !== 2);
                  const pairIdx = Math.floor(idx / 3);
                  const posInGroup = idx % 3;

                  if (isFullWidth) {
                    return (
                      <div key={idx} className="mb-1">
                        <div className="overflow-hidden bg-background-200">
                          <GalleryImg
                            src={img.src}
                            alt={img.caption}
                            className="w-full h-auto object-cover object-center"
                          />
                        </div>
                        <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-2 mb-6">
                          {img.caption}
                        </p>
                      </div>
                    );
                  }

                  // For pair images (position 1 and 2 in each group of 3)
                  // Only render when it's the first of a pair (position 1)
                  if (posInGroup === 1) {
                    const nextImg = galleryImages[idx + 1];
                    return (
                      <div key={`pair-${pairIdx}`} className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-1">
                        <div>
                          <div className="overflow-hidden bg-background-200">
                            <GalleryImg
                              src={img.src}
                              alt={img.caption}
                              className="w-full h-auto object-cover object-center"
                            />
                          </div>
                          <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-2 mb-6">
                            {img.caption}
                          </p>
                        </div>
                        {nextImg && (
                          <div>
                            <div className="overflow-hidden bg-background-200">
                              <GalleryImg
                                src={nextImg.src}
                                alt={nextImg.caption}
                                className="w-full h-auto object-cover object-center"
                              />
                            </div>
                            <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-400 mt-2 mb-6">
                              {nextImg.caption}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Skip position 2 (already rendered as part of pair)
                  return null;
                })
              )}
            </div>
          )}

          {/* Video gallery */}
          {caseItem.videos && caseItem.videos.length > 0 && (
            <div className="mt-12 space-y-0">
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-6 pb-4 border-b border-background-300">
                Film / Motion — {caseItem.videos.length} videos
              </p>
              <div className="space-y-8">
                {caseItem.videos.map((v, i) => (
                  <VideoEmbed key={v.src || i} video={v} />
                ))}
              </div>
            </div>
          )}

          {/* Next Project */}
          {nextWork && nextWork.id !== id && (
            <div className="mt-20 pt-12 border-t-2 border-foreground-950">
              <p className="font-label text-[9px] tracking-[0.14em] uppercase text-foreground-400 mb-6">
                Next Project
              </p>
              <Link
                to={`/case/${nextWork.id}`}
                className="group block"
              >
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
                  <div className="md:col-span-5">
                    <div className="relative overflow-hidden bg-background-200">
                      <div className="aspect-[4/3]">
                        <img
                          src={nextWork.cover}
                          alt={nextWork.title}
                          className={`w-full h-full transition-transform duration-700 ease-out group-hover:scale-105 ${
                          isTextCover(nextWork.id)
                            ? 'object-contain object-center'
                            : 'object-cover object-center'
                        }`}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <span className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400">
                        {nextWork.year}
                      </span>
                      <span className="w-1 h-px bg-foreground-300" />
                      <span className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-400">
                        {nextWork.category}
                      </span>
                    </div>
                    <h3 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight tracking-[-0.03em] hover-colorful mb-3">
                      {nextWork.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-4">
                      {nextWork.meta}
                    </p>
                    <div className="inline-flex items-center gap-2 font-label text-[11px] tracking-[0.12em] uppercase text-primary-500 group-hover:text-primary-600 transition-colors">
                      View Project
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}

          {/* Back to works list */}
          <div className="mt-16 pt-8 border-t border-background-300 flex items-center justify-between gap-4">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                sessionStorage.setItem('scrollToArchive', 'true');
                navigate('/');
              }}
              className="inline-flex items-center gap-2 font-label text-[11px] tracking-[0.12em] uppercase text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
              Back to All Works
            </a>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 font-label text-[11px] tracking-[0.12em] uppercase text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              Contact
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}